/**
 * Build the RUM→APM trace-propagation origin list shared by the browser
 * (`allowedTracingUrls`) and React Native (`firstPartyHosts`) SDK configs
 * (ADR-068). Derived from the app's existing API-URL config — never a hard-coded
 * literal — so a client fetch to the backend carries trace headers and the RUM
 * session stitches to the backend APM trace.
 *
 * The matcher object shape (`{ match, propagatorTypes }`) is accepted by both the
 * browser `allowedTracingUrls` and the RN `firstPartyHosts` config, so one helper
 * serves every surface. Propagator style is `tracecontext` + `datadog` (both), so
 * a future backend2 alignment has a header to read.
 */
export type PropagatorType = 'tracecontext' | 'datadog' | 'b3' | 'b3multi';

export interface TracingOriginMatcher {
  match: string;
  propagatorTypes: PropagatorType[];
}

const DEFAULT_PROPAGATORS: PropagatorType[] = ['tracecontext', 'datadog'];

/**
 * Resolve the backend origin from an API URL into a single tracing-origin
 * matcher. Returns `[]` for an absent/invalid URL (fail-safe: no origin means no
 * propagation, never a throw) so an unconfigured client still boots.
 */
export function resolveTracingOrigins(
  apiUrl: string | undefined,
  propagatorTypes: PropagatorType[] = DEFAULT_PROPAGATORS,
): TracingOriginMatcher[] {
  if (!apiUrl) return [];
  let origin: string;
  try {
    origin = new URL(apiUrl).origin;
  } catch {
    return [];
  }
  return [{ match: origin, propagatorTypes }];
}
